import { FC, useRef, Fragment, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ContextHolder } from '@frontegg/rest-api'
import { Box, IconButton, ListItemIcon, ListItemText, Typography } from '@mui/material'
import { useAuth } from '@frontegg/react'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import theme from 'theme'

/* Utils */
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { accountSettingsSelector, nexaAIEnabledSelector, nexaAIInProgressSelector } from 'store/user/userSlice'
import { selectThemeMode, toggleThemeMode } from 'store/theme/themeSlice'
import { isThemeSwitcherEnabled } from 'theme/themeMode'
import { useNexaAIStatus } from 'hooks/useNexaAIStatus'
import { hasAnyRole, hasRole, Role } from 'lib/utils'
import {
  DATA_SOURCES_PATH,
  isIntegrationsPath,
  TICKETING_SYSTEMS_PATH
} from 'ui/pages/settings/integrations/integrationPaths'

/* Components */
import {
  Chip,
  Collapse,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  LogoWrapper,
  MainMenuWrapper,
  ModeSwitch,
  ModeThumb,
  SidebarContainer
} from './sidebar.styles'
import {
  AssetManagementIcon,
  AssetManagementActiveIcon,
  InsightsIcon,
  NexaAIIcon,
  SecurityStackMapIcon,
  SidebarHomeIcon,
  SidebarLibraryIcon,
  SidebarReportsIcon,
  SidebarSettingsIcon,
  SidebarSettingsActiveIcon,
  SidebarDocumentationIcon,
  SecurityStackMapActiveIcon,
  SidebarReportsActiveIcon,
  SidebarHomeActiveIcon,
  SidebarLibraryActiveIcon,
  InsightsActiveIcon
} from '../svg'
import ChevronRight from '../svg/ChevronRight'
import ComingSoonSvg from '../svg/ComingSoonSvg'
import NewSvg from '../svg/NewSvg'
import { Avatar } from '../profile/profile.styles'
import { PermissionTooltip } from '../tooltip'
import AccountSwitcher from './account-switcher/AccountSwitcher'
import { OnyxAITooltip } from 'ui/pages/cpi-details/sections/trends-tab/sections/OnyxAITooltip'

interface SubListItem {
  name: string
  /* A group row has none: it only opens the pages nested under it. */
  url?: string
  selected: boolean
  isComing?: boolean
  isNew?: boolean
  parent: string
  isToggle?: boolean
  toggleEnabled?: boolean
  toggleIcon?: JSX.Element
  onToggleClick?: () => void
  subList?: SubListItem[]
}

interface ListItem {
  name: string
  url?: string
  selected: boolean
  icon: JSX.Element
  isComing?: boolean
  isNew?: boolean
  isDisabled?: boolean
  disabledReason?: 'feature_off' | 'no_permission'
  subList?: SubListItem[]
}

const ModeSunIcon = (
  <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6 1.5V2.625M9.18198 2.81802L8.38649 3.61351M10.5 6H9.375M9.18198 9.18198L8.38649 8.38649M6 9.375V10.5M3.61351 8.38649L2.81802 9.18198M2.625 6H1.5M3.61351 3.61351L2.81802 2.81802M7.875 6C7.875 7.03553 7.03553 7.875 6 7.875C4.96447 7.875 4.125 7.03553 4.125 6C4.125 4.96447 4.96447 4.125 6 4.125C7.03553 4.125 7.875 4.96447 7.875 6Z"
      stroke="#A13B7F"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const ModeMoonIcon = (
  <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10.876 7.50194C10.2985 7.7429 9.6648 7.87597 9 7.87597C6.30761 7.87597 4.125 5.69336 4.125 3.00097C4.125 2.33617 4.25807 1.70245 4.49903 1.125C2.73783 1.8599 1.5 3.59838 1.5 5.62597C1.5 8.31836 3.68261 10.501 6.375 10.501C8.40259 10.501 10.1411 9.26314 10.876 7.50194Z"
      stroke="#D0C3CC"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const MAIN_MENU_WIDTH = 72
const SIDEBAR_WIDTH = 339
const TIMEOUT_DRAWER = 500
const DOCS_FEATURE_DISABLED = 'docs-disabled'

const isAssetManagementEnabled = process.env.REACT_APP_ENABLE_ASSET_MANAGEMENT === 'true'
const isTicketingSystemsEnabled = process.env.REACT_APP_ENABLE_TICKETING_SYSTEMS === 'true'

const Sidebar: FC = () => {
  const navigate = useNavigate()
  const currentLocation = useLocation()
  const dispatch = useAppDispatch()
  const accountSettings = useAppSelector(accountSettingsSelector)
  const themeMode = useAppSelector(selectThemeMode)
  const { user: authUser } = useAuth()

  const [open, setOpen] = useState(false)
  const [activeSubMenuIndex, setActiveSubMenuIndex] = useState<null | number>(null)
  const [activeMenuIndex, setActiveMenuIndex] = useState<null | number>(null)
  /* Group rows nested inside a submenu, by name. Unset means the open page decides. */
  const [openSubGroups, setOpenSubGroups] = useState<Record<string, boolean>>({})
  const thisTimeoutId = useRef<any>(null)

  // NexaAI state from Redux
  const nexaAIEnabled = useAppSelector(nexaAIEnabledSelector)
  const nexaAIInProgress = useAppSelector(nexaAIInProgressSelector)

  // Check if user has owner or admin role for NexaAI access
  const canAccessNexaAI = hasAnyRole([Role.Owner, Role.Admin], authUser)
  const isOwner = hasRole(Role.Owner, authUser)

  useNexaAIStatus()

  const menuItems: ListItem[] = useMemo(() => {
    const items: ListItem[] = [
      {
        name: 'Dashboard',
        url: '/dashboard',
        selected: currentLocation.pathname === '/dashboard',
        icon: <SidebarHomeIcon />
      },
      {
        name: 'Nexa AI Agent',
        url: '/nexa-ai',
        selected: currentLocation.pathname === '/nexa-ai',
        icon: <NexaAIIcon />,
        isDisabled: (!nexaAIEnabled && !nexaAIInProgress) || !canAccessNexaAI,
        disabledReason:
          !nexaAIEnabled && !nexaAIInProgress ? 'feature_off' : !canAccessNexaAI ? 'no_permission' : undefined
      },
      ...(isAssetManagementEnabled
        ? [
            {
              name: 'Security Stack Map',
              url: '/security-stack-map',
              selected: currentLocation.pathname === '/security-stack-map',
              icon: <SecurityStackMapIcon />
            },
            {
              name: 'Asset Management',
              selected: ['/asset-inventory', '/asset-coverage-analyzer'].includes(currentLocation.pathname),
              icon: <AssetManagementIcon />,
              subList: [
                {
                  name: 'Asset Inventory',
                  url: '/asset-inventory',
                  selected: currentLocation.pathname.startsWith('/asset-inventory'),
                  parent: 'Asset Management'
                },
                {
                  name: 'Asset Coverage Analyzer',
                  url: '/asset-coverage-analyzer',
                  selected: currentLocation.pathname === '/asset-coverage-analyzer',
                  isNew: false,
                  parent: 'Asset Management'
                }
              ]
            }
          ]
        : [
            {
              name: 'Stack Management',
              selected: ['/asset-coverage-analyzer', '/security-stack-map'].includes(currentLocation.pathname),
              icon: <SecurityStackMapIcon />,
              subList: [
                {
                  name: 'Security Stack Map',
                  url: '/security-stack-map',
                  selected: currentLocation.pathname === '/security-stack-map',
                  parent: 'Stack Management'
                },
                {
                  name: 'Asset Coverage Analyzer',
                  url: '/asset-coverage-analyzer',
                  selected: currentLocation.pathname === '/asset-coverage-analyzer',
                  isNew: false,
                  parent: 'Stack Management'
                }
              ]
            }
          ]),
      {
        name: 'Cyber Program Management',
        icon: <SidebarLibraryIcon />,
        selected:
          currentLocation.pathname.startsWith('/library') ||
          currentLocation.pathname.startsWith('/framework-configuration'),
        subList: [
          {
            name: 'CPI Library',
            url: '/library',
            selected: currentLocation.pathname === '/library',
            parent: 'Cyber Program Management'
          },
          {
            name: 'Framework Configuration',
            url: '/framework-configuration',
            selected: currentLocation.pathname.includes('/framework-configuration'),
            isNew: false,
            parent: 'Cyber Program Management'
          }
        ]
      },
      {
        name: 'Insights',
        icon: <InsightsIcon />,
        url: '/insights',
        selected: currentLocation.pathname === '/insights'
      },
      {
        name: 'Reports',
        selected: currentLocation.pathname.startsWith('/reports'),
        url: '/reports',
        icon: <SidebarReportsIcon />,
        isNew: false
      }
    ]
    return items
  }, [currentLocation, authUser, nexaAIEnabled, nexaAIInProgress, canAccessNexaAI])

  const menuItemsBottom: ListItem[] = useMemo(() => {
    const baseUrl = (ContextHolder.getContext().baseUrl as string)?.replace(/\/+$/, '')
    const initials = authUser?.name.split(' ') || ['']

    return [
      {
        name: 'Settings',
        selected:
          (currentLocation.pathname.startsWith('/settings') && currentLocation.pathname !== '/settings/profile') ||
          currentLocation.pathname === '/settings/integrations/data-sources',
        icon: <SidebarSettingsIcon />,
        subList: [
          {
            name: 'Account Management',
            url: '/settings/account-management',
            selected: currentLocation.pathname === '/settings/account-management',
            parent: 'Settings'
          },
          {
            name: 'User Management',
            url: hasAnyRole([Role.Demo, Role.Viewer, Role.CPIManager], authUser) ? '' : '/settings/user-management',
            selected: currentLocation.pathname === '/settings/user-management',
            parent: 'Settings'
          },
          ...(isTicketingSystemsEnabled
            ? [
                {
                  name: 'Integrations',
                  selected: isIntegrationsPath(currentLocation.pathname),
                  parent: 'Settings',
                  subList: [
                    {
                      name: 'Data Sources',
                      url: !hasRole(Role.Viewer, authUser) ? DATA_SOURCES_PATH : '',
                      selected: currentLocation.pathname === DATA_SOURCES_PATH,
                      parent: 'Integrations'
                    },
                    {
                      name: 'Ticketing Systems',
                      url: !hasRole(Role.Viewer, authUser) ? TICKETING_SYSTEMS_PATH : '',
                      selected: currentLocation.pathname === TICKETING_SYSTEMS_PATH,
                      parent: 'Integrations'
                    }
                  ]
                }
              ]
            : [
                {
                  name: 'Integrations',
                  url: !hasRole(Role.Viewer, authUser) ? '/settings/integrations/data-sources' : '',
                  selected: currentLocation.pathname === '/settings/integrations/data-sources',
                  parent: 'Settings'
                }
              ]),
          {
            name: 'Audit Log',
            url: hasAnyRole([Role.Owner, Role.Admin], authUser) ? '/settings/audit-log' : '',
            selected: currentLocation.pathname === '/settings/audit-log',
            parent: 'Settings'
          }
        ]
      },
      {
        name: authUser?.name || '',
        selected: currentLocation.pathname === '/settings/profile',
        icon: (
          <Avatar alt={authUser?.name || ''} src={authUser?.profileImage || ''}>
            {initials.map((n) => n[0])}
          </Avatar>
        ),
        subList: [
          ...(isThemeSwitcherEnabled
            ? [
                {
                  name: 'Mode',
                  url: '',
                  selected: false,
                  parent: authUser?.name || '',
                  isToggle: true,
                  toggleEnabled: themeMode === 'dark',
                  onToggleClick: () => dispatch(toggleThemeMode())
                }
              ]
            : []),
          {
            name: 'User Profile',
            url: '/settings/profile',
            selected: currentLocation.pathname === '/settings/profile',
            parent: authUser?.name || ''
          },
          {
            name: 'Documentation',
            url: hasRole(Role.Demo, authUser)
              ? ''
              : accountSettings?.features?.docsAccessDisabledAt
              ? DOCS_FEATURE_DISABLED
              : 'https://onyxia.zendesk.com/hc',
            selected: false,
            parent: authUser?.name || ''
          },
          {
            name: 'Logout',
            url: `${baseUrl}/oauth/logout?post_logout_redirect_uri=${window.location}`,
            selected: false,
            parent: authUser?.name || ''
          }
        ]
      }
    ]
  }, [currentLocation, authUser, accountSettings, themeMode, dispatch])

  const handleMenuItemClick = (
    e: any,
    url: string,
    index: null | number = null,
    isSub: boolean,
    subIndex: number | null = null
  ) => {
    e.preventDefault()
    e.stopPropagation()

    if (document.fullscreenElement !== null) {
      document.exitFullscreen().catch((e) => e)
    }

    if (!url && isSub) return

    if (!url) {
      setActiveSubMenuIndex(subIndex === activeSubMenuIndex ? null : subIndex)
      setActiveMenuIndex(activeMenuIndex === index ? null : index)

      return
    }

    if (activeSubMenuIndex) setActiveSubMenuIndex(null)
    navigate(url)

    setOpen(false)
  }

  const closeWhenNotSelectedSubmenu = () => {
    const assetManagement = menuItems.find((n) => n.name === 'Asset Management' || n.name === 'Stack Management')
    const cyberPerformanceManagement = menuItems.find((n) => n.name === 'Cyber Program Management')
    const settings = menuItemsBottom.find((n) => n.name === 'Settings')
    const profile = menuItemsBottom.find((n) => n.name === authUser?.name || '')

    if (
      !assetManagement?.selected &&
      !cyberPerformanceManagement?.selected &&
      !settings?.selected &&
      !profile?.selected
    ) {
      setActiveMenuIndex(null)
    }
  }

  const onMouseEnter = () => {
    if (thisTimeoutId.current) {
      clearTimeout(thisTimeoutId.current)
    }

    thisTimeoutId.current = setTimeout(() => {
      setOpen(true)
      closeWhenNotSelectedSubmenu()
    }, TIMEOUT_DRAWER)
  }

  const onMouseLeave = () => {
    if (thisTimeoutId.current) {
      clearTimeout(thisTimeoutId.current)
    }

    thisTimeoutId.current = setTimeout(() => {
      setOpen(false)
      closeWhenNotSelectedSubmenu()
    }, TIMEOUT_DRAWER)
  }

  const getIcon = (selected: boolean, name: string, icon: JSX.Element) => {
    if (name === 'Dashboard') {
      return selected ? <SidebarHomeActiveIcon /> : icon
    }
    if (name === 'Security Stack Map' || name === 'Stack Management') {
      return selected ? <SecurityStackMapActiveIcon /> : icon
    }
    if (name === 'Asset Management') {
      return selected ? <AssetManagementActiveIcon /> : icon
    }
    if (name === 'Cyber Program Management') {
      return selected ? <SidebarLibraryActiveIcon /> : icon
    }
    if (name === 'Insights') {
      return selected ? <InsightsActiveIcon /> : icon
    }
    if (name === 'Reports') {
      return selected ? <SidebarReportsActiveIcon /> : icon
    }
    if (name === 'Settings') {
      return selected ? <SidebarSettingsActiveIcon /> : icon
    }
    if (name === authUser?.name) {
      const initials = authUser?.name.split(' ') || ['']

      return selected ? (
        <Avatar alt={authUser?.name || ''} src={authUser?.profileImage || ''} active>
          {initials.map((n) => n[0])}
        </Avatar>
      ) : (
        <Avatar alt={authUser?.name || ''} src={authUser?.profileImage || ''}>
          {initials.map((n) => n[0])}
        </Avatar>
      )
    }

    return icon
  }

  const renderItems = useMemo(() => {
    return menuItems.map(
      ({ icon, name, url, selected, subList, isComing, isNew, isDisabled, disabledReason }, index) => {
        const expanded = !url && activeMenuIndex === index && open

        const listItemContent = (
          <ListItem
            disablePadding
            isselected={selected ? 'true' : 'false'}
            onClick={(e) => !isDisabled && handleMenuItemClick(e, url || '', index, false)}
            expanded={expanded ? 'true' : 'false'}
            isopen={open ? 'true' : 'false'}
            sx={{
              pointerEvents: isDisabled ? 'none' : 'auto',
              marginBottom: 0
            }}
          >
            <ListItemButton
              selected={selected}
              isexpanded={open ? 'true' : 'false'}
              hassubmenu={subList && subList.length > 0 ? 'true' : 'false'}
              isselected={selected ? 'true' : 'false'}
              disableTouchRipple
              disabled={isDisabled}
              sx={{
                '&.Mui-disabled': {
                  opacity: 1
                },
                '& .MuiListItemIcon-root svg path': {
                  fill: isDisabled ? '#7F747C !important' : undefined
                },
                '& .MuiListItemText-root .MuiTypography-root': {
                  color: isDisabled
                    ? '#7F747C !important'
                    : selected
                      ? theme.colors.navItemActiveFg
                      : theme.colors.navText,
                  textOverflow: 'ellipsis'
                }
              }}
            >
              <ListItemIcon>{getIcon(selected, name, icon)}</ListItemIcon>
              {open && <ListItemText primary={name} />}

              {open && isComing && (
                <Chip isNew={false}>
                  {isComing ? <ComingSoonSvg /> : <NewSvg />}
                  <Typography className="chip-title">{isComing ? 'COMING SOON' : 'NEW'}</Typography>
                </Chip>
              )}
              {open && isNew && (
                <Chip isNew={true}>
                  <NewSvg />
                  <Typography className="chip-title">NEW</Typography>
                </Chip>
              )}
            </ListItemButton>
            <Box
              id="show-arrow-delay"
              className={`arrow-icon ${index === activeMenuIndex ? 'rotate' : 'no-rotate'} ${
                selected && 'is-selected'
              }`}
              sx={{
                display: open && subList ? 'block' : 'none',
                visibility: open && subList ? 'visible' : 'hidden',
                width: open && subList ? '24px' : '0'
              }}
            >
              <ChevronRight />
            </Box>
          </ListItem>
        )

        // Tooltip content based on user permissions and feature state
        const getTooltipContent = () => {
          // Users without access always see restricted message, regardless of feature state
          if (!canAccessNexaAI) {
            return (
              <>
                <Typography
                  sx={{
                    fontFamily: theme.typography.fontFamily.primary,
                    fontSize: '12px',
                    fontWeight: theme.typography.fontWeight.extraThick,
                    color: '#e9e0e4',
                    lineHeight: 1.4
                  }}
                >
                  Access Restricted
                </Typography>
                <Typography
                  sx={{
                    fontFamily: theme.typography.fontFamily.primary,
                    fontSize: '10px',
                    fontWeight: theme.typography.fontWeight.normal,
                    color: '#d0c3cc',
                    lineHeight: 1.4
                  }}
                >
                  Only Owners and Admins can access Nexa AI.
                </Typography>
              </>
            )
          }
          // Owner when feature is off - can enable it
          if (disabledReason === 'feature_off' && isOwner) {
            return (
              <>
                <Typography
                  sx={{
                    fontFamily: theme.typography.fontFamily.primary,
                    fontSize: '12px',
                    fontWeight: theme.typography.fontWeight.extraThick,
                    color: '#e9e0e4',
                    lineHeight: 1.4
                  }}
                >
                  Nexa AI Agent is turned off
                </Typography>
                <Typography
                  sx={{
                    fontFamily: theme.typography.fontFamily.primary,
                    fontSize: '10px',
                    fontWeight: theme.typography.fontWeight.normal,
                    color: '#d0c3cc',
                    lineHeight: 1.4,
                    whiteSpace: 'nowrap'
                  }}
                >
                  You can enable it anytime in Account Settings.
                </Typography>
              </>
            )
          }
          // Admin when feature is off - cannot enable, must contact owner
          if (disabledReason === 'feature_off') {
            return (
              <>
                <Typography
                  sx={{
                    fontFamily: theme.typography.fontFamily.primary,
                    fontSize: '12px',
                    fontWeight: theme.typography.fontWeight.extraThick,
                    color: '#e9e0e4',
                    lineHeight: 1.4
                  }}
                >
                  Nexa AI Agent is turned off
                </Typography>
                <Typography
                  sx={{
                    fontFamily: theme.typography.fontFamily.primary,
                    fontSize: '10px',
                    fontWeight: theme.typography.fontWeight.normal,
                    color: '#d0c3cc',
                    lineHeight: 1.4
                  }}
                >
                  Contact your Account Owner to enable it.
                </Typography>
              </>
            )
          }
          // Fallback
          return (
            <>
              <Typography
                sx={{
                  fontFamily: theme.typography.fontFamily.primary,
                  fontSize: '12px',
                  fontWeight: theme.typography.fontWeight.extraThick,
                  color: '#e9e0e4',
                  lineHeight: 1.4
                }}
              >
                Access Restricted
              </Typography>
              <Typography
                sx={{
                  fontFamily: theme.typography.fontFamily.primary,
                  fontSize: '10px',
                  fontWeight: theme.typography.fontWeight.normal,
                  color: '#d0c3cc',
                  lineHeight: 1.4
                }}
              >
                Only Owners and Admins can access Nexa AI.
              </Typography>
            </>
          )
        }

        return (
          <Fragment key={name}>
            {isDisabled && name === 'Nexa AI Agent' ? (
              <OnyxAITooltip
                title={getTooltipContent()}
                placement="right"
                followCursor
                enterDelay={200}
                leaveDelay={100}
                enterNextDelay={200}
              >
                <Box sx={{ pointerEvents: 'auto', marginBottom: `${theme.spacing.small}px` }}>{listItemContent}</Box>
              </OnyxAITooltip>
            ) : (
              listItemContent
            )}

            {open && (
              <Collapse in={expanded}>
                {subList &&
                  subList.map((subListItem, subIndex) => {
                    const parent = menuItems.find((n) => subListItem.parent === n.name)

                    return (
                      <ListItem
                        disablePadding
                        isselected={subListItem.selected ? 'true' : 'false'}
                        key={subListItem.name}
                        className="sublist-item"
                        isopen={open ? 'true' : 'false'}
                      >
                        <ListItemButton
                          className="sublist-item-button"
                          onClick={(e) => handleMenuItemClick(e, subListItem.url || '', index, true, subIndex)}
                          isselected={subListItem.selected ? 'true' : 'false'}
                          parentselected={parent && parent.selected ? 'true' : 'false'}
                        >
                          <ListItemText primary={subListItem.name} />
                        </ListItemButton>

                        {isComing && (
                          <Chip isNew={false}>
                            <ComingSoonSvg />
                            <Typography className="chip-title">COMING SOON</Typography>
                          </Chip>
                        )}
                        {isNew && (
                          <Chip isNew={true}>
                            <NewSvg />
                            <Typography className="chip-title">NEW</Typography>
                          </Chip>
                        )}
                      </ListItem>
                    )
                  })}
              </Collapse>
            )}
          </Fragment>
        )
      }
    )
  }, [open, menuItems, activeMenuIndex, currentLocation, authUser])

  const renderItemsFooter = useMemo(() => {
    return menuItemsBottom.map(({ icon, name, url, selected, subList, isComing, isNew }, index) => {
      const expanded = !url && activeMenuIndex === index + 7 && open

      return (
        <Fragment key={name}>
          {open && (
            <Collapse in={expanded} className="footer-collapse">
              {subList &&
                subList.map((subListItem, subIndex) => {
                  const handleClick = (e: any) => {
                    if (subListItem.name === 'Documentation') {
                      window.open('https://onyxia.zendesk.com/hc', '_blank')
                    } else if (subListItem.name === 'Logout') {
                      const baseUrl = (ContextHolder.getContext().baseUrl as string)?.replace(/\/+$/, '')
                      window.location.href = `${baseUrl}/oauth/logout?post_logout_redirect_uri=${window.location}`
                    } else {
                      handleMenuItemClick(e, subListItem.url || '', null, true, subIndex)
                    }
                  }
                  const parent = menuItems.find((n) => subListItem.parent === n.name)

                  /* A row that holds pages of its own rather than opening one, such as Integrations. */
                  if (subListItem.subList) {
                    const groupOpen = openSubGroups[subListItem.name] ?? subListItem.selected

                    return (
                      <Fragment key={subListItem.name}>
                        <ListItem
                          className="sublist-item footer-sublist-item"
                          disablePadding
                          isselected={subListItem.selected ? 'true' : 'false'}
                          isopen={open ? 'true' : 'false'}
                        >
                          <ListItemButton
                            className="sublist-item-button footer-sublist-item-button"
                            isselected={subListItem.selected ? 'true' : 'false'}
                            parentselected="false"
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              setOpenSubGroups((current) => ({ ...current, [subListItem.name]: !groupOpen }))
                            }}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              paddingRight: `${theme.spacing.xSmall}px !important`
                            }}
                          >
                            <ListItemText primary={subListItem.name} />
                            <Box className={`arrow-icon ${groupOpen ? 'rotate' : 'no-rotate'}`}>
                              <ChevronRight />
                            </Box>
                          </ListItemButton>
                        </ListItem>

                        <Collapse in={groupOpen} className="footer-collapse">
                          {subListItem.subList.map((nested) => (
                            <ListItem
                              className="sublist-item footer-sublist-item nested-sublist-item"
                              key={nested.name}
                              disablePadding
                              isselected={nested.selected ? 'true' : 'false'}
                              isopen={open ? 'true' : 'false'}
                            >
                              {nested.url ? (
                                <ListItemButton
                                  onClick={(e) => handleMenuItemClick(e, nested.url || '', null, true, subIndex)}
                                  className="sublist-item-button footer-sublist-item-button nested-sublist-item-button"
                                  isselected={nested.selected ? 'true' : 'false'}
                                  parentselected={subListItem.selected ? 'true' : 'false'}
                                >
                                  <ListItemText primary={nested.name} />
                                </ListItemButton>
                              ) : (
                                <PermissionTooltip
                                  PopperProps={{
                                    sx: {
                                      marginTop: `-${theme.spacing.sm}px !important`,
                                      zIndex: '100000000000 !important'
                                    }
                                  }}
                                  title={
                                    <Box className="tooltip-wrapper-permissions">
                                      <Typography className="tooltip-text">Insufficient permissions</Typography>
                                    </Box>
                                  }
                                >
                                  <ListItemButton
                                    disabled
                                    className="footer-sublist-item-button nested-sublist-item-button"
                                    sx={{ pointerEvents: 'unset !important' }}
                                  >
                                    <ListItemText primary={nested.name} />
                                  </ListItemButton>
                                </PermissionTooltip>
                              )}
                            </ListItem>
                          ))}
                        </Collapse>
                      </Fragment>
                    )
                  }

                  // Handle toggle items (like Nexa AI)
                  if (subListItem.isToggle) {
                    return (
                      <ListItem
                        className="sublist-item footer-sublist-item nexa-ai-toggle-item"
                        key={subListItem.name}
                        disablePadding
                        isselected="false"
                        isopen={open ? 'true' : 'false'}
                        sx={{
                          background: theme.colors.navSubItemBg,
                          borderRadius: `${theme.attributes.borderRadius.quaternary}`,
                          marginBottom: `${theme.spacing.half}px`
                        }}
                      >
                        <ListItemButton
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            subListItem.onToggleClick?.()
                          }}
                          className="sublist-item-button footer-sublist-item-button"
                          isselected="false"
                          parentselected="false"
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            paddingRight: `${theme.spacing.xSmall}px !important`
                          }}
                        >
                          <ListItemText primary={subListItem.name} />
                          <ModeSwitch
                            checked={subListItem.toggleEnabled}
                            icon={<ModeThumb>{ModeSunIcon}</ModeThumb>}
                            checkedIcon={<ModeThumb>{ModeMoonIcon}</ModeThumb>}
                            onClick={(e) => {
                              e.stopPropagation()
                              subListItem.onToggleClick?.()
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    )
                  }

                  return (
                    <ListItem
                      className="sublist-item footer-sublist-item"
                      key={subListItem.name}
                      disablePadding
                      isselected={subListItem.selected ? 'true' : 'false'}
                      isopen={open ? 'true' : 'false'}
                    >
                      {subListItem.url && subListItem.url !== DOCS_FEATURE_DISABLED ? (
                        <>
                          <ListItemButton
                            onClick={handleClick}
                            className="sublist-item-button footer-sublist-item-button footer-sublist-button"
                            isselected={subListItem.selected ? 'true' : 'false'}
                            parentselected={parent && parent.selected ? 'true' : 'false'}
                          >
                            <ListItemText primary={subListItem.name} />
                            {subListItem.name === 'Documentation' && <SidebarDocumentationIcon />}
                          </ListItemButton>
                          {isComing && (
                            <Chip isNew={false}>
                              <ComingSoonSvg />
                              <Typography className="chip-title">COMING SOON</Typography>
                            </Chip>
                          )}
                          {isNew && (
                            <Chip isNew={true}>
                              <NewSvg />
                              <Typography className="chip-title">NEW</Typography>
                            </Chip>
                          )}
                        </>
                      ) : subListItem.url === DOCS_FEATURE_DISABLED ? (
                        <ListItemButton
                          disabled
                          className="footer-sublist-item-button"
                          sx={{
                            paddingLeft:
                              name === authUser?.name ? `${theme.spacing.lg}px !important` : `${theme.spacing.sm}px`,
                            pointerEvents: 'unset !important'
                          }}
                        >
                          <ListItemText primary={subListItem.name} />
                          <OpenInNewIcon />
                        </ListItemButton>
                      ) : (
                        <>
                          <PermissionTooltip
                            PopperProps={{
                              sx: {
                                marginTop: `-${theme.spacing.sm}px !important`,
                                zIndex: '100000000000 !important'
                              }
                            }}
                            title={
                              <>
                                <Box className="tooltip-wrapper-permissions">
                                  <Typography className="tooltip-text">Insufficient permissions</Typography>
                                </Box>
                              </>
                            }
                          >
                            <ListItemButton
                              disabled
                              className="footer-sublist-item-button"
                              sx={{
                                paddingLeft:
                                  name === authUser?.name
                                    ? `${theme.spacing.lg}px !important`
                                    : `${theme.spacing.sm}px`,
                                pointerEvents: 'unset !important'
                              }}
                            >
                              <ListItemText primary={subListItem.name} />
                              {subListItem.name === 'Documentation' && <OpenInNewIcon />}
                            </ListItemButton>
                          </PermissionTooltip>
                          {isComing && (
                            <Chip isNew={false}>
                              <ComingSoonSvg />
                              <Typography className="chip-title">COMING SOON</Typography>
                            </Chip>
                          )}
                          {isNew && (
                            <Chip isNew={true}>
                              <NewSvg />
                              <Typography className="chip-title">NEW</Typography>
                            </Chip>
                          )}
                        </>
                      )}
                    </ListItem>
                  )
                })}
            </Collapse>
          )}

          <ListItem
            disablePadding
            isselected={selected ? 'true' : 'false'}
            onClick={(e) => handleMenuItemClick(e, url || '', index + 7, false)}
            expanded={expanded ? 'true' : 'false'}
            className="footer-list-item"
            sx={{
              background: selected && name === authUser?.name && !open ? 'transparent !important' : '',
              width: name === authUser?.name && !open ? '48px !important' : ''
            }}
            isopen={open ? 'true' : 'false'}
          >
            <ListItemButton
              selected={selected}
              isexpanded={open ? 'true' : 'false'}
              hassubmenu={subList && subList.length > 0 ? 'true' : 'false'}
              isselected={selected ? 'true' : 'false'}
              disableTouchRipple
              className="footer-list-item-button"
            >
              <ListItemIcon>{getIcon(selected, name, icon)}</ListItemIcon>
              {open && (
                <>
                  {authUser && name === authUser.name ? (
                    <Box className="avatar-details-wrapper">
                      <ListItemText primary={name} />
                      <ListItemText primary={authUser.email} />
                    </Box>
                  ) : (
                    <ListItemText primary={name} />
                  )}
                </>
              )}

              {open && isComing && (
                <Chip isNew={false}>
                  {isComing ? <ComingSoonSvg /> : <NewSvg />}
                  <Typography className="chip-title">{isComing ? 'COMING SOON' : 'NEW'}</Typography>
                </Chip>
              )}
              {open && isNew && (
                <Chip isNew={true}>
                  <NewSvg />
                  <Typography className="chip-title">NEW</Typography>
                </Chip>
              )}
            </ListItemButton>
            <Box
              id="show-arrow-delay"
              className={`arrow-icon ${index + 7 === activeMenuIndex ? 'rotate' : 'no-rotate'} ${
                selected && 'is-selected'
              }`}
              sx={{
                display: open && subList ? 'block' : 'none',
                visibility: open && subList ? 'visible' : 'hidden',
                width: open && subList ? '24px' : '0'
              }}
            >
              <ChevronRight />
            </Box>
          </ListItem>
        </Fragment>
      )
    })
  }, [open, menuItemsBottom, menuItems, activeMenuIndex, openSubGroups, accountSettings, currentLocation, authUser])

  return (
    <Drawer
      className="sidebar-drawer"
      sx={{
        width: open ? `${SIDEBAR_WIDTH}px` : `${MAIN_MENU_WIDTH}px`,
        overflowX: open ? 'hidden' : 'auto'
      }}
    >
      <SidebarContainer
        className="sidebar-container"
        sx={{ width: open ? `${SIDEBAR_WIDTH}px` : `${MAIN_MENU_WIDTH}px`, overflowX: open ? 'hidden' : 'auto' }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <MainMenuWrapper
          className="sidebar_main-menu-wrapper"
          isHome={currentLocation.pathname === '/'}
          isexpanded={open ? 'true' : 'false'}
        >
          <LogoWrapper className="sidebar_logo-wrapper" isopen={open ? 'true' : 'false'}>
            <IconButton
              className="sidebar_onyxia-logo-wrapper"
              onClick={(e) => handleMenuItemClick(e, '/', null, false)}
            >
              <svg width="32" height="36" viewBox="0 0 32 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M29.3302 27.5454C30.4533 25.4537 31.0955 23.0767 31.0955 20.5678C31.0955 20.4273 31.0926 20.2868 31.0896 20.1463C31.0882 20.0999 31.0853 20.055 31.0838 20.0087C31.0795 19.9145 31.0766 19.8218 31.0708 19.7277C31.0679 19.6741 31.0635 19.6205 31.0591 19.5654C31.0548 19.4814 31.0475 19.3959 31.0417 19.3119C31.0359 19.2554 31.0301 19.1989 31.0243 19.141C31.017 19.0599 31.0083 18.9788 30.9996 18.8991C30.9923 18.8412 30.985 18.7832 30.9778 18.7253C30.9676 18.6456 30.9574 18.5659 30.9458 18.4877C30.9385 18.4298 30.9298 18.3733 30.9197 18.3153C30.908 18.2357 30.895 18.1574 30.8819 18.0778C30.8717 18.0227 30.8615 17.9662 30.8514 17.9112C30.8368 17.8301 30.8209 17.7504 30.8049 17.6707C30.7947 17.6171 30.7831 17.5621 30.7715 17.5085C30.754 17.4274 30.7351 17.3448 30.7163 17.2623C30.7046 17.213 30.693 17.1623 30.6814 17.1131C30.6596 17.0247 30.6364 16.9363 30.6146 16.8494C30.6029 16.806 30.5913 16.7625 30.5797 16.7205C30.5521 16.6162 30.5216 16.5134 30.4911 16.4105C30.4838 16.3845 30.4765 16.3569 30.4693 16.3323C30.3894 16.0701 30.3036 15.8123 30.2107 15.5559C30.2005 15.5284 30.1903 15.5008 30.1787 15.4733C30.1424 15.3748 30.1061 15.2778 30.0668 15.1793C30.0523 15.1416 30.0363 15.104 30.0218 15.0663C29.9869 14.9794 29.9506 14.8925 29.9143 14.807C29.8968 14.765 29.878 14.7244 29.8605 14.6839C29.8242 14.6013 29.7879 14.5202 29.7516 14.4391C29.7312 14.3971 29.7109 14.3536 29.692 14.3116C29.6542 14.2319 29.6164 14.1537 29.5772 14.074C29.5569 14.032 29.5351 13.99 29.5147 13.948C29.4755 13.8684 29.4348 13.7916 29.3942 13.7134C29.3724 13.6728 29.3506 13.6322 29.3288 13.5917C29.2866 13.512 29.2431 13.4338 29.198 13.3556C29.1777 13.3179 29.1559 13.2803 29.1341 13.2411C29.0876 13.16 29.0397 13.0789 28.9917 12.9978C28.9714 12.9659 28.9525 12.9312 28.9321 12.8978C28.8784 12.8095 28.8232 12.7226 28.768 12.6357C28.752 12.611 28.736 12.585 28.72 12.5589C28.6503 12.4517 28.5791 12.3445 28.5079 12.2388C28.505 12.2344 28.5021 12.2315 28.5006 12.2272C28.3466 12.0041 28.1883 11.7839 28.0241 11.5695C28.0081 11.5478 27.9907 11.5261 27.9747 11.5044C27.9108 11.4218 27.8454 11.3392 27.78 11.2567C27.7539 11.2248 27.7277 11.1944 27.703 11.1625C27.6449 11.0915 27.5868 11.0205 27.5272 10.951C27.4967 10.9163 27.4662 10.88 27.4357 10.8453C27.3805 10.7815 27.3238 10.7164 27.2672 10.6526C27.2337 10.615 27.1989 10.5773 27.1655 10.5411C27.1103 10.4788 27.055 10.4194 26.9998 10.3615C26.9635 10.3224 26.9272 10.2832 26.8894 10.2456C26.8357 10.1876 26.7804 10.1326 26.7252 10.0761C26.686 10.037 26.6468 9.99789 26.609 9.95878C26.5538 9.90518 26.4986 9.85159 26.4434 9.79799C26.4027 9.75888 26.362 9.71977 26.3199 9.68066C26.2661 9.62851 26.2109 9.57926 26.1557 9.52856C26.1121 9.48945 26.07 9.44889 26.0264 9.41123C25.9727 9.36343 25.9174 9.31563 25.8622 9.26783C25.8172 9.22727 25.7722 9.18816 25.7257 9.14905C25.6734 9.10415 25.6211 9.06214 25.5688 9.01868C25.5237 8.98102 25.4787 8.94336 25.4322 8.9057C25.4336 8.90714 25.4336 8.90714 25.4336 8.90714C22.5875 6.60978 18.9219 5.30755 15.0413 5.54801C15.0994 5.5987 17.5678 7.65996 19.9229 8.24661C22.0993 8.78981 22.7531 9.4402 22.7531 9.4402C22.7531 9.4402 24.822 11.177 22.3172 13.0224C22.3172 13.0224 21.5705 13.5352 21.6707 14.5608C21.6765 14.6013 21.6853 14.6404 21.6925 14.681L21.6911 14.6781C21.694 14.694 21.6969 14.71 21.7012 14.7259C21.707 14.7534 21.7114 14.7795 21.7172 14.807C21.7259 14.8447 21.7347 14.8838 21.7434 14.9229C21.7506 14.949 21.7579 14.9765 21.7637 15.004C21.7739 15.0431 21.7855 15.0822 21.7957 15.1199C21.8029 15.1474 21.8102 15.1749 21.8189 15.2025C21.8305 15.2416 21.8422 15.2807 21.8538 15.3198C21.8625 15.3473 21.8712 15.3748 21.8799 15.4023C21.893 15.4415 21.9046 15.4806 21.9192 15.5197C21.9279 15.5472 21.9366 15.5747 21.9468 15.6008C21.9598 15.6414 21.9744 15.6805 21.9889 15.7196C21.9976 15.7471 22.0078 15.7732 22.018 15.7992C22.0325 15.8398 22.047 15.8789 22.0615 15.918C22.0717 15.9441 22.0819 15.9702 22.0906 15.9962C22.1066 16.0383 22.1226 16.0774 22.1385 16.1179C22.1487 16.1425 22.1574 16.1657 22.1676 16.1903C22.1836 16.2324 22.201 16.2729 22.2185 16.3149C22.2272 16.3366 22.2359 16.3598 22.2446 16.3816C22.2635 16.4265 22.2809 16.4699 22.2998 16.5134C22.3071 16.5322 22.3158 16.551 22.3231 16.5713C22.3434 16.622 22.3652 16.6713 22.3855 16.7205C22.3986 16.7509 22.4102 16.7813 22.4233 16.8103C22.4393 16.8494 22.4567 16.89 22.4713 16.9276C22.4843 16.9581 22.4974 16.9885 22.509 17.0189C22.5207 17.0464 22.5323 17.0739 22.5439 17.1015C22.557 17.1348 22.57 17.1667 22.5831 17.1985C22.5918 17.2217 22.6006 17.2449 22.6107 17.2666C22.6253 17.3057 22.6398 17.3419 22.6543 17.3781C22.6587 17.3941 22.6645 17.4086 22.6703 17.423C22.6906 17.4752 22.7081 17.5244 22.7255 17.5737C23.3357 19.3134 23.5057 21.0849 23.0132 22.7478C22.9914 22.8232 22.9681 22.8999 22.942 22.9738C22.8664 23.1998 22.7793 23.4229 22.679 23.643C22.4771 24.0848 22.2228 24.5165 21.9119 24.9351C21.8581 25.0075 21.8029 25.08 21.7463 25.1509C21.6344 25.29 21.5167 25.4262 21.3976 25.5551C20.9167 26.0708 20.3806 26.4966 19.8052 26.8356C19.4449 27.0471 19.0716 27.2238 18.6865 27.3672C18.4555 27.4541 18.2216 27.5265 17.9848 27.5888C17.4327 27.7351 16.8647 27.8148 16.2966 27.8307C16.2152 27.8336 16.1339 27.8351 16.0525 27.8351C14.9977 27.8336 13.9444 27.6149 12.9753 27.1948C12.9012 27.1615 12.8271 27.1282 12.7545 27.0934C12.6063 27.0239 12.4625 26.95 12.3201 26.8704C12.2489 26.8312 12.1777 26.7907 12.108 26.7487C11.9685 26.6647 11.8319 26.5763 11.6983 26.4821C11.63 26.4372 11.5646 26.388 11.4978 26.3402C11.104 26.0461 10.7379 25.7101 10.4081 25.3335C10.3529 25.2712 10.2992 25.2089 10.2469 25.1437C10.1408 25.0133 10.0391 24.8786 9.94175 24.741C9.89235 24.6715 9.84441 24.6005 9.79792 24.5295C9.65844 24.3137 9.53059 24.0906 9.41146 23.8545C9.37368 23.7763 9.33446 23.6981 9.29813 23.6184C9.18772 23.3765 9.08892 23.1259 9.0032 22.8666C8.9233 22.6204 8.85501 22.3712 8.80271 22.1192C8.73152 21.786 8.68357 21.4485 8.66033 21.1096C8.6487 20.9401 8.64289 20.7706 8.64289 20.6011C8.64289 20.5171 8.64434 20.4316 8.6487 20.3476C8.65742 20.0941 8.67921 19.8421 8.71263 19.5915C8.79399 19.0063 8.94364 18.4312 9.16011 17.8808C9.19062 17.8026 9.22259 17.7243 9.256 17.6461C9.42453 17.2594 9.62503 16.8842 9.85894 16.5293C9.95192 16.3873 10.0493 16.2468 10.1539 16.1121C10.257 15.976 10.3645 15.8427 10.4793 15.7152C10.5912 15.5863 10.7103 15.4603 10.8338 15.3401C11.0692 15.1083 11.3191 14.8925 11.5835 14.6955C12.044 14.3507 12.5453 14.0596 13.0727 13.8307C13.1482 13.7988 13.2252 13.767 13.3008 13.7365C13.4548 13.6772 13.6102 13.6207 13.7671 13.5714C13.9241 13.5222 14.0839 13.4773 14.2437 13.4396C14.5023 13.3759 14.7101 13.3252 14.8771 13.315C14.944 13.3121 15.0035 13.315 15.0558 13.3252C15.1096 13.3353 15.1575 13.3541 15.1982 13.3831C15.3217 13.47 15.3929 13.6453 15.4234 13.9567C15.4278 14.0074 15.4321 14.0639 15.435 14.1233C15.4525 14.484 15.3508 14.8751 15.22 15.2097C15.3842 15.0735 15.56 14.9548 15.743 14.8534C16.3532 14.5115 17.0492 14.3478 17.7494 14.2928C17.9601 14.2768 18.1708 14.2696 18.38 14.271C18.9205 14.2768 19.6106 14.5535 20.1278 14.4912C20.1627 14.4869 20.1975 14.4825 20.2295 14.4767C20.2411 14.4753 20.2527 14.4724 20.2644 14.4709C20.2832 14.4666 20.3036 14.4637 20.321 14.4594C20.3355 14.455 20.3486 14.4507 20.3631 14.4478C20.3762 14.4434 20.3922 14.4405 20.4053 14.4376C20.4198 14.4318 20.4329 14.4275 20.4474 14.4231C20.4576 14.4188 20.4707 14.4159 20.4794 14.4101C20.4954 14.4058 20.5084 14.4 20.5215 14.3942C20.5302 14.3898 20.5404 14.3869 20.5491 14.3826C20.5622 14.3753 20.5753 14.3681 20.5869 14.3609C20.5942 14.3565 20.6014 14.3536 20.6087 14.3507C20.6218 14.342 20.6334 14.3348 20.645 14.3261C20.6508 14.3217 20.6581 14.3174 20.6639 14.3145C20.6755 14.3058 20.6857 14.2971 20.6959 14.2884C20.7017 14.2841 20.7075 14.2797 20.7118 14.2768C20.7235 14.2653 20.7322 14.2551 20.7423 14.2464C20.7467 14.2421 20.7511 14.2377 20.7554 14.2334C20.7641 14.2232 20.7743 14.2117 20.783 14.2015C20.7859 14.1972 20.7888 14.1928 20.7932 14.1899C20.8019 14.1769 20.8106 14.1653 20.8179 14.1537C20.8208 14.1494 20.8237 14.1465 20.8252 14.1421C20.8339 14.1291 20.8426 14.1161 20.8499 14.1016C20.8513 14.0987 20.8528 14.0958 20.8557 14.0929C20.8629 14.0784 20.8702 14.0639 20.876 14.048C20.8775 14.0451 20.8789 14.0422 20.8804 14.0393C20.8876 14.0233 20.8934 14.0074 20.9007 13.9915C20.9022 13.9886 20.9022 13.9871 20.9036 13.9857C20.9094 13.9669 20.9167 13.9495 20.9225 13.9306V13.9277C21.0329 13.5902 21.0416 13.1412 21.3714 12.6675C21.7288 12.1547 22.2519 11.7405 22.3216 11.1987C22.3521 10.9684 22.2998 10.7164 22.1182 10.4238C21.492 9.40978 19.49 9.27797 18.4541 8.84631C17.7945 8.56964 17.1494 8.25386 16.5319 7.89173C16.4971 7.87145 16.4622 7.85262 16.4273 7.83089C16.3532 7.78743 16.2806 7.74108 16.208 7.69617C16.1527 7.66141 16.0975 7.62809 16.0423 7.59333C16.0249 7.58174 16.0089 7.57015 15.9915 7.55856C15.8811 7.48759 15.7721 7.41516 15.6646 7.34128C15.5411 7.25582 15.4176 7.16746 15.297 7.0762C13.879 6.01733 12.5148 4.57894 11.9787 2.86099C11.9322 3.30568 11.8871 3.73155 11.8784 4.13424C11.877 4.20087 11.877 4.26751 11.8784 4.33414C11.8842 4.92804 11.9845 5.47413 12.2939 5.97098C11.5196 5.95215 10.8106 5.66679 10.1699 5.21919C9.88509 5.02219 9.61195 4.79187 9.3548 4.53983C9.22549 4.41236 9.10054 4.28054 8.9785 4.14438C7.93971 2.98266 7.14935 1.49502 6.62486 0.381104C6.61905 0.531751 6.61615 0.683846 6.61615 0.83739C6.61324 1.60366 6.67717 2.39021 6.81664 3.16662C6.84425 3.32017 6.87476 3.47661 6.90817 3.63015C7.21327 5.01495 7.77117 6.34325 8.63708 7.4224C6.85878 8.41465 5.31583 9.75888 4.08671 11.3392C3.8034 11.7057 3.53607 12.0823 3.28763 12.472C3.12055 12.7298 2.96219 12.9949 2.81255 13.2629C2.66145 13.5323 2.52052 13.8061 2.38541 14.0827C2.25174 14.3609 2.1268 14.6419 2.01057 14.9272C1.89289 15.2126 1.78537 15.5008 1.68658 15.7935C1.58633 16.0861 1.49771 16.3816 1.41635 16.68C1.37566 16.8291 1.33789 16.9798 1.30157 17.129C1.12287 17.8837 1.00228 18.6528 0.944165 19.4336C0.915107 19.8247 0.902031 20.2144 0.904937 20.6011C0.912202 21.8918 1.08945 23.1534 1.4178 24.3615C1.44976 24.4832 1.48463 24.6034 1.52095 24.7236C3.30652 30.7119 8.77946 35.3254 15.3493 35.605C15.6137 35.6166 15.8782 35.6209 16.1397 35.618C16.6787 35.6122 17.2133 35.576 17.7393 35.5123C17.9616 35.4848 18.1839 35.4529 18.4061 35.4167C18.5485 35.392 18.6924 35.3674 18.8333 35.3399C18.9597 35.3138 19.0846 35.2878 19.2081 35.2602C19.4304 35.211 19.6512 35.1545 19.8692 35.0951C20.2614 34.9894 20.6479 34.8662 21.0286 34.7286C21.3845 34.5997 21.7332 34.4592 22.0761 34.3042C22.2475 34.2274 22.416 34.1463 22.5846 34.0623C22.8243 33.9435 23.0611 33.8189 23.295 33.6871C23.4432 33.6046 23.59 33.5205 23.7352 33.4322C24.0113 33.2642 24.2815 33.0874 24.5459 32.9035C24.6898 32.8035 24.8336 32.7007 24.9745 32.5949C25.3726 32.298 25.7562 31.9808 26.1252 31.6462C26.3083 31.4781 26.4884 31.3072 26.6642 31.1305C27.3674 30.4265 28.0023 29.6544 28.5588 28.8288C28.6968 28.6216 28.8304 28.4116 28.9597 28.1972C29.0876 27.9828 29.2125 27.767 29.3302 27.5454Z"
                  fill={theme.colors.navLogo}
                />
              </svg>
            </IconButton>
            {open && (
              <Box className="expanded-logo-wrapper">
                <Typography className="onyxia-logo-title">Onyxia</Typography>
              </Box>
            )}
          </LogoWrapper>

          <List
            sx={{ maxWidth: open ? SIDEBAR_WIDTH : MAIN_MENU_WIDTH }}
            className="main-list"
            aria-labelledby="nested-list-subheader"
          >
            {renderItems}
          </List>
        </MainMenuWrapper>

        <List
          sx={{ maxWidth: open ? SIDEBAR_WIDTH : MAIN_MENU_WIDTH }}
          className="footer-list"
          isexpanded={open ? 'true' : 'false'}
          aria-labelledby="nested-list-footer-subheader"
        >
          <AccountSwitcher isExpanded={open} />
          {renderItemsFooter}
        </List>
      </SidebarContainer>
    </Drawer>
  )
}

export default Sidebar
